import React, { useRef, useState, useEffect } from 'react'
import { X, UploadCloud } from 'react-feather'
import CSVIcon from 'public/images/support/csv-icon.svg'
import PDFIcon from 'public/images/support/pdf-icon.svg'

const mimeFromExtension = (ext: string): string => {
  switch (ext) {
    case 'csv':
      return 'text/csv'
    case 'pdf':
      return 'application/pdf'
    case 'jpg':
    case 'jpeg':
      return 'image/jpeg'
    case 'png':
      return 'image/png'
    case 'gif':
      return 'image/gif'
    case 'mp4':
      return 'video/mp4'
    case 'webm':
      return 'video/webm'
    case 'avi':
      return 'video/x-msvideo'
    case 'mov':
      return 'video/quicktime'
    default:
      return 'application/octet-stream'
  }
}

const extractMeta = (filename: string) => {
  const ext = filename.split('.').pop()?.toLowerCase() || ''
  const type = mimeFromExtension(ext)
  return { name: filename, type }
}

interface FileItem {
  id?: number
  url: string
  name: string
  type: string
  isBlob?: boolean
  fileObject?: File
}

interface MultiFileUploadPreview {
  initialUrls: { fileUrl: string; fileName: string; id: number }[]
  onFilesChange?: (files: File[], id?: number) => void
  readOnly?: boolean
  maxFiles?: number
  maxFileSizeMB?: number
  onError?: (message: string) => void
}

const MultiFileUploadPreview: React.FC<MultiFileUploadPreview> = ({
  initialUrls,
  onFilesChange,
  readOnly,
  maxFiles = 5,
  maxFileSizeMB = 10,
  onError
}) => {
  const [files, setFiles] = useState<FileItem[]>(() =>
    initialUrls.map(({ fileUrl, fileName, id }) => {
      const meta = extractMeta(fileName)
      return { url: fileUrl, ...meta, id: id }
    })
  )

  const [localError, setLocalError] = useState<string | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const inputRef = useRef<HTMLInputElement | null>(null)

  const showError = (message: string) => {
    setLocalError(message)
    onError?.(message)
  }

  const validateFiles = (newFiles: File[]): boolean => {
    const totalFiles = files.length + newFiles.length

    if (totalFiles > maxFiles) {
      showError(`You can only upload up to ${maxFiles} files.`)
      return false
    }

    const oversized = newFiles.find(f => f.size > maxFileSizeMB * 1024 * 1024)
    if (oversized) {
      showError(`"${oversized.name}" exceeds the maximum size of ${maxFileSizeMB}MB.`)
      return false
    }

    setLocalError(null)
    return true
  }

  const handleUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return
    addFiles(e.target.files)
    e.target.value = ''
  }

  const handleRemove = (file: FileItem) => {
    if (file.isBlob) {
      URL.revokeObjectURL(file.url)
    }
    setFiles(prev => {
      const newFiles = prev.filter(f => f.url !== file.url)
      const uploadedFiles = newFiles.filter(f => f.isBlob && f.fileObject).map(f => f.fileObject as File)
      onFilesChange?.(uploadedFiles, file?.id)
      return newFiles
    })
  }

  const checkForDuplicateUpload = (newFiles: FileItem[]): boolean => {
    return newFiles.some(newFile =>
      files.some(prevFile => prevFile.name === newFile.name && prevFile.type === newFile.type)
    )
  }

  const addFiles = (fileList: FileList) => {
    const newFiles = Array.from(fileList)

    if (!validateFiles(newFiles)) return

    const processed = newFiles.map(file => ({
      url: URL.createObjectURL(file),
      name: file.name,
      type: file.type,
      isBlob: true,
      fileObject: file
    }))
    const isDuplicate = checkForDuplicateUpload(processed)
    if (!isDuplicate) {
      setFiles(prev => {
        const newFiles = [...prev, ...processed]
        const uploadedFiles = newFiles.filter(f => f.isBlob && f.fileObject).map(f => f.fileObject as File)
        onFilesChange?.(uploadedFiles)
        return newFiles
      })
    }
  }

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    if (readOnly) return
    setIsDragging(false)
    if (e.dataTransfer.files) {
      addFiles(e.dataTransfer.files)
    }
  }

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = () => {
    setIsDragging(false)
  }

  const getPreviewIcon = (file: FileItem) => {
    const ext = file.name.split('.').pop()?.toLowerCase()

    if (file.type.startsWith('image/')) {
      return <img src={file.url} alt={file.name} className='w-24 h-24 object-cover rounded' />
    }

    if (file.type.startsWith('video/')) {
      return <video src={file.url} className='w-24 h-24 object-cover rounded' controls muted preload='metadata' />
    }

    if (ext === 'csv') return <CSVIcon />
    if (ext === 'pdf') return <PDFIcon />
    return <img src='/icons/file-icon.png' alt='File' className='w-24 h-24' />
  }

  return (
    <div className='flex flex-col gap-2'>
      <div
        className='flex gap-4 flex-wrap items-start'
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
      >
        {!readOnly && (
          <div
            onClick={() => {
              setLocalError('')
              inputRef.current?.click()
            }}
            className={`w-24 h-24 border-2 border-dashed rounded flex items-center justify-center cursor-pointer flex-col transition ${
              isDragging ? 'border-blue-500 text-blue-500' : 'border-gray-300 hover:border-blue-500 hover:text-blue-500'
            }`}
            title='Upload or drag files'
          >
            <UploadCloud size={24} />
            <span className='text-xs mt-1'>Upload</span>
            <input type='file' ref={inputRef} multiple hidden onChange={handleUpload} />
          </div>
        )}
        {files.map(file => (
          <div key={file.url} className='relative group w-24'>
            {!readOnly && (
              <button
                onClick={() => handleRemove(file)}
                className='absolute top-0 right-0 z-10 bg-white rounded-full p-1 shadow-md hover:bg-red-100 transition'
                title='Remove file'
              >
                <X size={14} className='text-red-500' />
              </button>
            )}

            <a
              href={file.url}
              download={file.name}
              target='_blank'
              rel='noopener noreferrer'
              className='flex flex-col items-center group-hover:opacity-80 transition'
            >
              {getPreviewIcon(file)}
              {/* <span className='text-sm mt-1 truncate w-24 text-center'>{file.name}</span> */}
            </a>
          </div>
        ))}
      </div>

      {localError && <div className='text-red-500 text-sm mt-1'>{localError}</div>}
    </div>
  )
}

export default MultiFileUploadPreview
